/**
 * Merges MRZ readings across live camera frames.
 *
 * The split is by what the MRZ itself protects: passport number and birth
 * date carry their own check digits, so they lock in from the first frame
 * that validates them. Names and sex carry NO check digit — ICAO left them
 * unprotected — so they need CONSENSUS: two frames must agree before the
 * reading is trusted. If the scan never reaches a confirmed, complete
 * result, a settle deadline (measured from the first frame that read
 * anything) releases the best reading so far rather than scanning forever.
 */

const NAME_KEYS = ["legal_first_name", "legal_last_name"] as const;
const GENDER_KEY = "gender";
const CHECK_DIGIT_KEYS = ["passport_number", "date_of_birth"] as const;

const CONSENSUS_VOTES = 2;
const SETTLE_MS = 12_000;

interface AccumulatorResult {
  values: Record<string, string>;
  /** All five fields present with consensus names, or the deadline passed. */
  done: boolean;
  /** How many of the five scannable fields the current best reading covers. */
  fieldsFound: number;
}

interface NamePair {
  first: string;
  last: string;
}

function bestVote(
  votes: Map<string, number>,
  minVotes: number,
): string | null {
  let best: { key: string; votes: number } | null = null;
  for (const [key, count] of votes) {
    if (count >= minVotes && (best === null || count > best.votes)) {
      best = { key, votes: count };
    }
  }
  return best?.key ?? null;
}

export class ScanAccumulator {
  private readonly fixed: Record<string, string> = {};
  private readonly nameVotes = new Map<string, number>();
  private readonly genderVotes = new Map<string, number>();
  private firstHitAt: number | null = null;

  addFrame(values: Record<string, string>, now: number): void {
    if (Object.keys(values).length > 0 && this.firstHitAt === null) {
      this.firstHitAt = now;
    }
    for (const key of CHECK_DIGIT_KEYS) {
      const value = values[key];
      // First validated reading wins — these are check-digit-backed, so a
      // later disagreement is far likelier a worse frame than a correction.
      if (value && !(key in this.fixed)) {
        this.fixed[key] = value;
      }
    }
    const first = values[NAME_KEYS[0]] ?? "";
    const last = values[NAME_KEYS[1]] ?? "";
    if (first || last) {
      const key = JSON.stringify([first, last]);
      this.nameVotes.set(key, (this.nameVotes.get(key) ?? 0) + 1);
    }
    const gender = values[GENDER_KEY];
    if (gender) {
      this.genderVotes.set(gender, (this.genderVotes.get(gender) ?? 0) + 1);
    }
  }

  private namePair(minVotes: number): NamePair | null {
    const key = bestVote(this.nameVotes, minVotes);
    if (key === null) return null;
    const [first, last] = JSON.parse(key) as [string, string];
    return { first, last };
  }

  private compose(minVotes: number): Record<string, string> {
    const values = { ...this.fixed };
    const names = this.namePair(minVotes);
    if (names?.first) values[NAME_KEYS[0]] = names.first;
    if (names?.last) values[NAME_KEYS[1]] = names.last;
    const gender = bestVote(this.genderVotes, minVotes);
    if (gender !== null) values[GENDER_KEY] = gender;
    return values;
  }

  result(now: number): AccumulatorResult {
    const confirmed = this.compose(CONSENSUS_VOTES);
    const complete =
      CHECK_DIGIT_KEYS.every((key) => key in confirmed) &&
      NAME_KEYS.every((key) => key in confirmed) &&
      GENDER_KEY in confirmed;
    if (complete) {
      return {
        values: confirmed,
        done: true,
        fieldsFound: Object.keys(confirmed).length,
      };
    }
    const settled =
      this.firstHitAt !== null && now - this.firstHitAt >= SETTLE_MS;
    const values = settled ? this.compose(1) : confirmed;
    return { values, done: settled, fieldsFound: Object.keys(values).length };
  }
}
